const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || "http://localhost:8000/api";
const ASSETS_BASE_URL = process.env.REACT_APP_ASSETS_BASE_URL || "http://localhost:8000";

const AUTH_TOKEN_KEY = "wamglammz_admin_token";
const AUTH_USER_KEY = "wamglammz_admin_user";
const USER_TOKEN_KEY = "wamglammz_user_token";
const USER_SESSION_KEY = "wamglammz_user_session";

export function getImageUrl(path) {
  if (!path) return "";
  if (path.startsWith("http://") || path.startsWith("https://") || path.startsWith("data:")) {
    return path;
  }
  const cleanPath = path.replace(/\\/g, "/");
  const serverBase = ASSETS_BASE_URL;
  return `${serverBase}${cleanPath.startsWith("/") ? "" : "/"}${cleanPath}`;
}

export function getAuthToken() {
  return localStorage.getItem(AUTH_TOKEN_KEY);
}

export function saveAuthSession(user) {
  console.log("[Auth Sync] Saving administrative token session parameters:", user);
  if (user?.token) {
    localStorage.setItem(AUTH_TOKEN_KEY, user.token);
  }
  localStorage.setItem(AUTH_USER_KEY, JSON.stringify(user || {}));
}

export function clearAuthSession() {
  console.log("[Auth Sync] Purging administrative session token keys from local storage ledger.");
  localStorage.removeItem(AUTH_TOKEN_KEY);
  localStorage.removeItem(AUTH_USER_KEY);
}

export function getSavedUser() {
  try {
    return JSON.parse(localStorage.getItem(AUTH_USER_KEY) || "null");
  } catch {
    return null;
  }
}

export function getUserToken() {
  return localStorage.getItem(USER_TOKEN_KEY);
}

export function saveUserSession(session) {
  const data = session?.data || session || {};
  console.log("[Auth Sync] Preserving user transaction payload sequence maps:", data);
  if (data.token) {
    localStorage.setItem(USER_TOKEN_KEY, data.token);
  }
  localStorage.setItem(USER_SESSION_KEY, JSON.stringify(data));
}

export function clearUserSession() {
  console.log("[Auth Sync] Clearing user credentials profile.");
  localStorage.removeItem(USER_TOKEN_KEY);
  localStorage.removeItem(USER_SESSION_KEY);
}

export function getSavedUserSession() {
  try {
    return JSON.parse(localStorage.getItem(USER_SESSION_KEY) || "null");
  } catch {
    return null;
  }
}

export async function apiRequest(path, options = {}) {
  const { tokenType = "admin", ...requestOptions } = options;
  const token = tokenType === "user" ? getUserToken() : tokenType === "none" ? null : getAuthToken();

  console.log(`[Network Request] Routing outbound call to URL: ${API_BASE_URL}${path}`, {
    method: requestOptions.method || "GET",
    tokenType,
    hasToken: !!token
  });

  const headers = {
    "Content-Type": "application/json",
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...(requestOptions.headers || {}),
  };

  const response = await fetch(`${API_BASE_URL}${path}`, {
    ...requestOptions,
    headers,
  });

  const text = await response.text();
  const payload = text ? JSON.parse(text) : null;

  if (!response.ok) {
    console.error(`[Network Error] API Execution failed for route ${path}:`, payload);
    throw new Error(payload?.message || payload?.error || "Inbound response pipeline failure.");
  }

  return payload;
}

export async function apiFormRequest(path, formData, options = {}) {
  const { tokenType = "user", ...requestOptions } = options;
  const token = tokenType === "user" ? getUserToken() : tokenType === "none" ? null : getAuthToken();

  console.log(`[Multipart Form] Processing binary stream layout routing to: ${API_BASE_URL}${path}`);

  const headers = {
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...(requestOptions.headers || {}),
  };

  const response = await fetch(`${API_BASE_URL}${path}`, {
    ...requestOptions,
    method: requestOptions.method || "POST",
    headers,
    body: formData,
  });

  const text = await response.text();
  const payload = text ? JSON.parse(text) : null;

  if (!response.ok) {
    throw new Error(payload?.message || payload?.error || "Multipart file streaming request failed.");
  }

  return payload;
}

export const adminApi = {
  login: (email, password) =>
    apiRequest("/admin/login", {
      method: "POST",
      body: JSON.stringify({ email, password }),
    }),
  signup: (name, email, password) =>
    apiRequest("/admin/signup", {
      method: "POST",
      body: JSON.stringify({ name, email, password }),
    }),
  profile: () => apiRequest("/admin/profile"),
  updateProfile: (profile) =>
    apiRequest("/admin/update-profile", {
      method: "PUT",
      body: JSON.stringify(profile),
    }),
  changePassword: (passwords) =>
    apiRequest("/admin/change-password", {
      method: "PUT",
      body: JSON.stringify(passwords),
    }),
  categories: () => apiRequest("/admin/category?gender=all"),
  createCategory: (category) =>
    apiRequest("/admin/category", {
      method: "POST",
      body: JSON.stringify(category),
    }),
  updateCategory: (categoryId, category) =>
    apiRequest(`/admin/category/${categoryId}`, {
      method: "PUT",
      body: JSON.stringify(category),
    }),
  deleteCategory: (categoryId) =>
    apiRequest(`/admin/category/${categoryId}`, {
      method: "DELETE",
    }),
  subcategories: () => apiRequest("/admin/subcategories"),
  createSubcategory: (categoryId, subcategory) =>
    apiRequest(`/admin/category/${categoryId}/subcategory`, {
      method: "POST",
      body: JSON.stringify(subcategory),
    }),
  updateSubcategory: (subcatId, subcategory) =>
    apiRequest(`/admin/subcategory/${subcatId}`, {
      method: "PUT",
      body: JSON.stringify(subcategory),
    }),
  deleteSubcategory: (subcatId) =>
    apiRequest(`/admin/subcategory/${subcatId}`, {
      method: "DELETE",
    }),
  services: () => apiRequest("/admin/services"),
  createService: (service) =>
    apiRequest("/admin/services", {
      method: "POST",
      body: JSON.stringify(service),
    }),
  updateService: (serviceId, service) =>
    apiRequest(`/admin/services/${serviceId}`, {
      method: "PUT",
      body: JSON.stringify(service),
    }),
  deleteService: (serviceId) =>
    apiRequest(`/admin/services/${serviceId}`, {
      method: "DELETE",
    }),
  bookings: (page = 1, limit = 20) => apiRequest(`/admin/list/booking?page=${page}&limit=${limit}`),
  rescheduleBooking: (bookingId, date, slotTime) =>
    apiRequest(`/bookings/reschedule/${bookingId}`, {
      method: "PUT",
      body: JSON.stringify({ date, slot_time: slotTime }),
    }),
  cancelBooking: (bookingId, cancelledBy = "admin", adminNote = "") =>
    apiRequest(`/bookingss/${bookingId}/cancel`, {
      method: "POST",
      body: JSON.stringify({ cancelled_by: cancelledBy, admin_note: adminNote }),
    }),
  users: () => apiRequest("/admin/users"),
  userDetails: (userId) => apiRequest(`/admin/users/${userId}`),
  updateUserStatus: (userId, status) =>
    apiRequest(`/admin/users/${userId}/status`, {
      method: "PATCH",
      body: JSON.stringify({ status }),
    }),
  artists: () => apiRequest("/admin/artists"),
  artistDetails: (artistId) => apiRequest(`/admin/artist/${artistId}`),
  toggleSuspendArtist: (artistId) =>
    apiRequest(`/admin/artist/${artistId}/toggle-suspend`, {
      method: "PATCH",
    }),
  toggleVerifyArtist: (artistId) =>
    apiRequest(`/admin/artist/${artistId}/toggle-verify`, {
      method: "PATCH",
    }),
  artistServicesByCategory: (artistId, category) => apiRequest(`/admin/artist/${artistId}/services/${encodeURIComponent(category)}`),
  addServiceToArtist: (artistId, service) =>
    apiRequest(`/admin/artist/${artistId}/services`, {
      method: "POST",
      body: JSON.stringify(service),
    }),
  updateArtistService: (serviceId, service) =>
    apiRequest(`/admin/artist/services/${serviceId}`, {
      method: "PUT",
      body: JSON.stringify(service),
    }),
  addArtistDaySchedule: (artistId, schedule) =>
    apiRequest(`/admin/artist/${artistId}/day-schedules`, {
      method: "POST",
      body: JSON.stringify(schedule),
    }),
  updateArtistSchedule: (artistId, schedule) =>
    apiRequest(`/admin/artist/${artistId}/schedule`, {
      method: "PUT",
      body: JSON.stringify(schedule),
    }),
  stats: () => apiRequest("/admin/admin/stats"),
  getArtistWeeklySchedule: (artistId) => apiRequest(`/artist/${artistId}/schedule`),
  updateArtistWeeklySchedule: (artistId, schedules) =>
    apiRequest(`/artist/schedule/${artistId}`, {
      method: "PUT",
      body: JSON.stringify({ schedules }),
    }),

  // SaaS Extender Methods
  getSaaSInvoices: () => apiRequest("/admin/saas/billing/invoices"),
  getNotificationSettings: () => apiRequest("/admin/saas/notifications/settings"),
  saveNotificationSettings: (payload) => apiRequest("/admin/saas/notifications/settings", { method: "PUT", body: JSON.stringify(payload) }),
  getClientNotes: (clientId) => apiRequest(`/admin/users/${clientId}/notes`),
  addClientNote: (clientId, note) => apiRequest(`/admin/users/${clientId}/notes`, { method: "POST", body: JSON.stringify({ note }) }),
  triggerStripeCheckout: (planType, returnUrl) => apiRequest("/admin/saas/billing/checkout", { method: "POST", body: JSON.stringify({ planType, returnUrl }) }),
  triggerStripePortal: (returnUrl) => apiRequest("/admin/saas/billing/portal", { method: "POST", body: JSON.stringify({ returnUrl }) }),
  uploadAdminImage: (formData) => apiFormRequest("/admin/upload/admin-image", formData, { tokenType: "admin" }),
};

export const publicApi = {
  privacyPolicyByPageName: (pageName) => apiRequest(`/privacy-policy/${pageName}`, { tokenType: "none" }),
  getProfileBySlug: (slug) => apiRequest(`/public/vendors/${slug}`, { tokenType: "none" }),
  getServicesBySlug: (slug) => apiRequest(`/public/vendors/${slug}/services`, { tokenType: "none" }),
  getAvailabilityBySlug: (slug, date) => apiRequest(`/public/vendors/${slug}/availability?date=${encodeURIComponent(date)}`, { tokenType: "none" }),
};

export const userApi = {
  getOtp: (phone) =>
    apiRequest("/get-otp", {
      method: "POST",
      tokenType: "none",
      body: JSON.stringify({ phone }),
    }),
  verifyOtp: (phone, otp) =>
    apiRequest("/verify-otp", {
      method: "POST",
      tokenType: "none",
      body: JSON.stringify({ phone, otp }),
    }),
  saveProfile: (profile) => {
    const formData = new FormData();
    Object.entries(profile).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== "") {
        formData.append(key, value);
      }
    });
    return apiFormRequest("/save-profile", formData, { tokenType: "user" });
  },
  profile: () => apiRequest("/profile", { tokenType: "user" }),
  countries: () => apiRequest("/locations/countries", { tokenType: "user" }),
  states: (countryId) => apiRequest(`/locations/states/${countryId}`, { tokenType: "user" }),
  cities: (stateId) => apiRequest(`/locations/cities/${stateId}`, { tokenType: "user" }),
  saveLocation: (location) =>
    apiRequest("/locations/save-user-location", {
      method: "POST",
      tokenType: "user",
      body: JSON.stringify(location),
    }),
  availableArtists: ({ selectedDate, slotId, userId, serviceIds = [] }) =>
    apiRequest("/artists/available", {
      method: "POST",
      tokenType: "none",
      body: JSON.stringify({ selectedDate, slotId, userId, serviceIds }),
    }),
  artistSlots: (artistId, date) => apiRequest(`/artist/${artistId}/slots?date=${encodeURIComponent(date)}`, { tokenType: "none" }),
  generateArtistSlots: (artistId, date, slotMinutes = 60) =>
    apiRequest(`/artist/${artistId}/generate-slots`, {
      method: "POST",
      tokenType: "none",
      body: JSON.stringify({ date, slotMinutes }),
    }),
  bookSlot: (artistId, payload) =>
    apiRequest(`/artist/${artistId}/book-slot`, {
      method: "POST",
      tokenType: "user",
      body: JSON.stringify(payload),
    }),
  bookings: (userId) => apiRequest(`/booking/${userId}`, { tokenType: "user" }),
  cart: (userId) => apiRequest(`/cart/${userId}`, { tokenType: "user" }),
  addToCart: (payload) =>
    apiRequest("/cart/add", {
      method: "POST",
      tokenType: "user",
      body: JSON.stringify(payload),
    }),
  updateCartQuantity: (payload) =>
    apiRequest("/cart/update", {
      method: "POST",
      tokenType: "user",
      body: JSON.stringify(payload),
    }),
  updateCartServices: (payload) =>
    apiRequest("/cart/update", {
      method: "PUT",
      tokenType: "user",
      body: JSON.stringify(payload),
    }),
  removeCartArtist: (payload) =>
    apiRequest("/cart/remove-artist", {
      method: "DELETE",
      tokenType: "user",
      body: JSON.stringify(payload),
    }),
  clearCart: () =>
    apiRequest("/cart/clear", {
      method: "DELETE",
      tokenType: "user",
    }),
  processPayment: (payload) =>
    apiRequest("/payment/process", {
      method: "POST",
      tokenType: "user",
      body: JSON.stringify(payload),
    }),
  createPaymentIntent: (payload) =>
    apiRequest("/payment/create-payment-intent", {
      method: "POST",
      tokenType: "user",
      body: JSON.stringify(payload),
    }),
  createBookingStripePaymentIntent: (payload) =>
    apiRequest("/bookings/stripe/create-payment-intent", {
      method: "POST",
      tokenType: "user",
      body: JSON.stringify(payload),
    }),
  favourites: (userId) => apiRequest(`/favourite/list/${userId}`, { tokenType: "user" }),
  toggleFavourite: (payload) =>
    apiRequest("/favourite/toggle", {
      method: "POST",
      tokenType: "user",
      body: JSON.stringify(payload),
    }),
  topArtists: (limit = 10) => apiRequest(`/topartists?limit=${limit}`, { tokenType: "user" }),
  reviews: (artistId) => apiRequest(`/review/${artistId}`, { tokenType: "user" }),
  saveFcmToken: (payload) =>
    apiRequest("/save-token", {
      method: "POST",
      tokenType: "none",
      body: JSON.stringify(payload),
    }),
  deleteFcmToken: (payload) =>
    apiRequest("/delete-token", {
      method: "POST",
      tokenType: "none",
      body: JSON.stringify(payload),
    }),
  sendNotification: (payload) =>
    apiRequest("/notifications", {
      method: "POST",
      tokenType: "none",
      body: JSON.stringify(payload),
    }),
};

export const vendorArtistApi = {
  // ── Auth ──────────────────────────────────────────────────────────────────
  getOtp: (email) =>
    apiRequest("/vendor-artist/get-otp", {
      method: "POST",
      tokenType: "none",
      body: JSON.stringify({ email }),
    }),
  verifyOtp: (email, otp) =>
    apiRequest("/vendor-artist/verify-otp", {
      method: "POST",
      tokenType: "none",
      body: JSON.stringify({ email, otp }),
    }),

  // ── Profile ───────────────────────────────────────────────────────────────
  saveProfile: (profile) => {
    const formData = new FormData();
    Object.entries(profile).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== "") {
        formData.append(key, value);
      }
    });
    return apiFormRequest("/vendor-artist/save-profile", formData, { tokenType: "admin" });
  },
  profile: () => apiRequest("/vendor-artist/profile", { tokenType: "admin" }),
  updateProfile: (payload) =>
    apiRequest("/vendor-artist/profile/update", {
      method: "PUT",
      tokenType: "admin",
      body: JSON.stringify(payload),
    }),
  changePassword: (payload) =>
    apiRequest("/vendor-artist/profile/change-password", {
      method: "PUT",
      tokenType: "admin",
      body: JSON.stringify(payload),
    }),

  // ── Image uploads ─────────────────────────────────────────────────────────
  uploadProfilePhoto: (formData) =>
    apiFormRequest("/vendor-artist/upload/profile-photo", formData, { tokenType: "admin" }),
  uploadPortfolioImage: (formData) =>
    apiFormRequest("/vendor-artist/upload/portfolio-image", formData, { tokenType: "admin" }),

  // ── Stats ─────────────────────────────────────────────────────────────────
  stats: () => apiRequest("/vendor-artist/stats", { tokenType: "admin" }),

  // ── Notifications ─────────────────────────────────────────────────────────
  notifications: () => apiRequest("/vendor-artist/notifications", { tokenType: "admin" }),
  getNotificationSettings: () => apiRequest("/vendor-artist/notifications/settings", { tokenType: "admin" }),
  saveNotificationSettings: (payload) =>
    apiRequest("/vendor-artist/notifications/settings", {
      method: "PUT",
      tokenType: "admin",
      body: JSON.stringify(payload),
    }),

  // ── Subscription / Stripe ─────────────────────────────────────────────────
  getSaaSPlans: () => apiRequest("/saas-plans", { tokenType: "none" }),
  getSaaSInvoices: () => apiRequest("/vendor-artist/billing/invoices", { tokenType: "admin" }),
  triggerStripeCheckout: (planType, returnUrl) =>
    apiRequest("/vendor-artist/billing/checkout", {
      method: "POST",
      tokenType: "admin",
      body: JSON.stringify({ planType, returnUrl }),
    }),
  triggerStripePortal: (returnUrl) =>
    apiRequest("/vendor-artist/billing/portal", {
      method: "POST",
      tokenType: "admin",
      body: JSON.stringify({ returnUrl }),
    }),

  // ── Categories ────────────────────────────────────────────────────────────
  categories: () => apiRequest("/vendor-artist/categories", { tokenType: "admin" }),
  createCategory: (category) =>
    apiRequest("/vendor-artist/categories", {
      method: "POST",
      tokenType: "admin",
      body: JSON.stringify(category),
    }),
  updateCategory: (id, category) =>
    apiRequest(`/vendor-artist/categories/${id}`, {
      method: "PUT",
      tokenType: "admin",
      body: JSON.stringify(category),
    }),
  deleteCategory: (id) =>
    apiRequest(`/vendor-artist/categories/${id}`, {
      method: "DELETE",
      tokenType: "admin",
    }),

  // ── Subcategories ─────────────────────────────────────────────────────────
  subcategories: () => apiRequest("/vendor-artist/subcategories", { tokenType: "admin" }),
  createSubcategory: (categoryId, subcategory) =>
    apiRequest(`/vendor-artist/categories/${categoryId}/subcategories`, {
      method: "POST",
      tokenType: "admin",
      body: JSON.stringify(subcategory),
    }),
  updateSubcategory: (id, subcategory) =>
    apiRequest(`/vendor-artist/subcategories/${id}`, {
      method: "PUT",
      tokenType: "admin",
      body: JSON.stringify(subcategory),
    }),
  deleteSubcategory: (id) =>
    apiRequest(`/vendor-artist/subcategories/${id}`, {
      method: "DELETE",
      tokenType: "admin",
    }),

  // ── Services ──────────────────────────────────────────────────────────────
  services: () => apiRequest("/vendor-artist/services", { tokenType: "admin" }),
  createService: (service) =>
    apiRequest("/vendor-artist/services", {
      method: "POST",
      tokenType: "admin",
      body: JSON.stringify(service),
    }),
  updateService: (id, service) =>
    apiRequest(`/vendor-artist/services/${id}`, {
      method: "PUT",
      tokenType: "admin",
      body: JSON.stringify(service),
    }),
  deleteService: (id) =>
    apiRequest(`/vendor-artist/services/${id}`, {
      method: "DELETE",
      tokenType: "admin",
    }),

  // ── Artists / Stylists ────────────────────────────────────────────────────
  artists: () => apiRequest("/vendor-artist/artists", { tokenType: "admin" }),
  createArtist: (artist) =>
    apiRequest("/vendor-artist/artists", {
      method: "POST",
      tokenType: "admin",
      body: JSON.stringify(artist),
    }),
  updateArtist: (id, artist) =>
    apiRequest(`/vendor-artist/artists/${id}`, {
      method: "PUT",
      tokenType: "admin",
      body: JSON.stringify(artist),
    }),
  deleteArtist: (id) =>
    apiRequest(`/vendor-artist/artists/${id}`, {
      method: "DELETE",
      tokenType: "admin",
    }),
  getArtistWeeklySchedule: (artistId) =>
    apiRequest(`/vendor-artist/artists/${artistId}/schedule`, { tokenType: "admin" }),
  updateArtistWeeklySchedule: (artistId, schedules) =>
    apiRequest(`/vendor-artist/artists/${artistId}/schedule`, {
      method: "PUT",
      tokenType: "admin",
      body: JSON.stringify({ schedules }),
    }),
  getArtistServices: (artistId) =>
    apiRequest(`/vendor-artist/artists/${artistId}/services`, { tokenType: "admin" }),
  addArtistService: (artistId, payload) =>
    apiRequest(`/vendor-artist/artists/${artistId}/services`, {
      method: "POST",
      tokenType: "admin",
      body: JSON.stringify(payload),
    }),
  updateArtistService: (serviceId, payload) =>
    apiRequest(`/vendor-artist/artist-services/${serviceId}`, {
      method: "PUT",
      tokenType: "admin",
      body: JSON.stringify(payload),
    }),

  // ── Bookings (vendor's view) ───────────────────────────────────────────────
  bookings: () => apiRequest("/vendor-artist/bookings", { tokenType: "admin" }),
  createBooking: (payload) =>
    apiRequest("/vendor-artist/bookings", {
      method: "POST",
      tokenType: "admin",
      body: JSON.stringify(payload),
    }),
  rescheduleBooking: (id, date, slotTime) =>
    apiRequest(`/vendor-artist/bookings/${id}/reschedule`, {
      method: "PUT",
      tokenType: "admin",
      body: JSON.stringify({ date, slot_time: slotTime }),
    }),
  cancelBooking: (id, cancelledBy = "artist", reason = "") =>
    apiRequest(`/vendor-artist/bookings/${id}/cancel`, {
      method: "POST",
      tokenType: "admin",
      body: JSON.stringify({ cancelled_by: cancelledBy, reason }),
    }),
  confirmBooking: (id) =>
    apiRequest(`/vendor-artist/bookings/${id}/confirm`, {
      method: "POST",
      tokenType: "admin",
    }),

  // ── Clients ───────────────────────────────────────────────────────────────
  clients: () => apiRequest("/vendor-artist/clients", { tokenType: "admin" }),
  getClientNotes: (clientId) =>
    apiRequest(`/vendor-artist/clients/${clientId}/notes`, { tokenType: "admin" }),
  addClientNote: (clientId, note) =>
    apiRequest(`/vendor-artist/clients/${clientId}/notes`, {
      method: "POST",
      tokenType: "admin",
      body: JSON.stringify({ note }),
    }),
  updateClientStatus: (clientId, status) =>
    apiRequest(`/vendor-artist/clients/${clientId}/status`, {
      method: "PATCH",
      tokenType: "admin",
      body: JSON.stringify({ status }),
    }),
};

// ─── Public Client Booking API (vendor-client slug-based booking) ─────────────
export const publicClientApi = {
  getClientOtp: (email) =>
    apiRequest("/public/vendors/booking/get-otp", {
      method: "POST",
      tokenType: "none",
      body: JSON.stringify({ email }),
    }),
  verifyClientOtp: (email, otp) =>
    apiRequest("/public/vendors/booking/verify-otp", {
      method: "POST",
      tokenType: "none",
      body: JSON.stringify({ email, otp }),
    }),
  confirmBooking: (payload) =>
    apiRequest("/public/vendors/booking/confirm", {
      method: "POST",
      tokenType: "user",
      body: JSON.stringify(payload),
    }),
  getBookingSlots: (artistId, date) =>
    apiRequest(`/public/vendors/booking/slots?artistId=${artistId}&date=${encodeURIComponent(date)}`, {
      tokenType: "none",
    }),
  getClientBookings: (userId) =>
    apiRequest(`/public/vendors/booking/${userId}/history`, { tokenType: "user" }),
  cancelBooking: (bookingId, reason = "") =>
    apiRequest(`/vendor-bookings/${bookingId}/cancel`, {
      method: "POST",
      tokenType: "user",
      body: JSON.stringify({ reason }),
    }),
  getBookingDetails: (bookingId) =>
    apiRequest(`/vendor-bookings/details/${bookingId}`, {
      tokenType: "user",
    }),
  getVendorWeeklySchedule: (artistId) =>
    apiRequest(`/public/vendors/booking/artists/${artistId}/schedule`, { tokenType: "none" }),
};